import React, { useState } from 'react';
import { AlertTriangleIcon, FileTextIcon, Loader2Icon, LockIcon, PhoneIcon, CreditCardIcon, LandmarkIcon } from 'lucide-react';
import { STEP_ESTIMATE, useFunnel } from '../../contexts/FunnelContext';
import { phones, siteConfig, isLive } from '../../data/siteConfig';
import type { StringKey } from '../../data/i18n';
import type { StepErrors } from '../../utils/validation';
import { stepErrors } from '../../utils/validation';
import { usd } from '../../utils/format';
import { fireAddToCart, fireInitiateCheckout, moveContentId } from '../../utils/metaPixel';
import { ChoiceCard } from './ChoiceCard';
import { TextField } from './TextField';
import { HelpAnswers } from './HelpAnswers';
interface DocRow {
  key: StringKey;
  who: string;
  when: StringKey;
}
export function StepReserve() {
  const {
    t,
    lang,
    data,
    update,
    quote,
    estimateAccepted,
    goTo,
    submit
  } = useFunnel();
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errors, setErrors] = useState<StepErrors>({});
  const checkoutReady = isLive() && siteConfig.depositCheckoutEnabled;
  if (!estimateAccepted) {
    return <div className="rounded-2xl border-2 border-magenta/40 bg-white p-6" data-id="element-866" data-mp="1gs2cn704ukfo">
        <AlertTriangleIcon className="h-8 w-8 text-magenta" aria-hidden="true" data-id="element-867" data-mp="1bv232m42qzjt" />
        <h1 className="mt-3 font-display text-2xl font-black text-ink" data-id="element-868" data-mp="16x0nwc9rrkdm">{t('staleTitle')}</h1>
        <p className="mt-2 text-[15px] text-ink-600" data-id="element-869" data-mp="24i50fax0l48h">{t('staleBody')}</p>
        <button type="button" onClick={() => goTo(STEP_ESTIMATE)} className="mt-5 h-12 rounded-xl bg-ink px-5 font-semibold text-white hover:bg-ink-700" data-id="element-870" data-mp="1zjv612j467nx">
          {t('staleBtn')}
        </button>
      </div>;
  }
  const docs: DocRow[] = [{
    key: 'docBooking',
    who: t('whoYou'),
    when: 'whenNow'
  }, ...(data.paymentMethod === 'card' ? [data.thirdPartyPayer ? {
    key: 'docThird' as StringKey,
    who: `${t('whoPayer')}${data.payerName ? `: ${data.payerName}` : ''}`,
    when: 'whenNow' as StringKey
  } : {
    key: 'docDeposit' as StringKey,
    who: t('whoCardholder'),
    when: 'whenNow' as StringKey
  }] : []), {
    key: 'docFull',
    who: data.thirdPartyPayer ? t('whoPayer') : t('whoCardholder'),
    when: 'whenLater'
  }, {
    key: 'docBol',
    who: t('whoYou'),
    when: 'whenLater'
  }];
  const goShopify = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const url = siteConfig.shopifyCheckoutUrl;
    if (!url) return;
    e.preventDefault();
    const params = {
      content_ids: [moveContentId(data.bedrooms)],
      content_category: data.homeType ?? 'unspecified',
      crew: quote.crew.used,
      packing: data.packing ?? 'unspecified',
      service_area: quote.zoneReasonKey
    };
    fireAddToCart(quote.deposit, `atc_${quote.hash}`, params);
    fireInitiateCheckout(quote.deposit, `ic_${quote.hash}`, {
      ...params,
      estimated_move_value: quote.laborHigh ?? 0
    });
    // Short delay so the pixel request leaves before the page navigates away.
    window.setTimeout(() => window.location.assign(url), 300);
  };
  const send = async () => {
    const e = stepErrors(8, data);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSending(true);
    setFailed(false);
    try {
      await submit();
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };
  return <div data-id="element-871" data-mp="30g4389neefh6">
      <div className="mb-7" data-id="element-872" data-mp="30gcejx3vhcq9">
        <h1 className="font-display text-3xl font-black leading-tight text-magenta sm:text-4xl" data-id="element-873" data-mp="30f2g36t3y2j3">{t('reserveTitle')}</h1>
        <p className="mt-2 text-base text-ink-600" data-id="element-874" data-mp="3fbs1li61c8d5">{t('reserveIntro')}</p>
        <p className="mt-2 font-mono text-xs text-ink-500" data-id="element-875" data-mp="3aea3kmtk3djx">
          {t('versionLabel')}: {quote.configVersion} · #{quote.hash}
        </p>
      </div>

      <section className="grid gap-6 md:grid-cols-2" data-id="element-876" data-mp="3k4uzu3lrc5zo">
        <fieldset data-id="element-877" data-mp="3k5ehdj7hwsd6">
          <legend className="mb-2 font-display text-lg font-extrabold text-ink" data-id="element-878" data-mp="3k6z10ds9uqw9">{t('payerTitle')}</legend>
          <div role="radiogroup" aria-label={t('payerTitle')} className="grid gap-2" data-id="element-879" data-mp="1wnh6uwfrlvx">
            <ChoiceCard size="sm" selected={!data.thirdPartyPayer} onSelect={() => update({
            thirdPartyPayer: false
          })} title={t('payerMe')} data-id="element-880" data-mp="3t36oi032ctph" />
            <ChoiceCard size="sm" selected={data.thirdPartyPayer} onSelect={() => update({
            thirdPartyPayer: true
          })} title={t('payerOther')} data-id="element-881" data-mp="l2tj0el139q4" />
          </div>
          {data.thirdPartyPayer && <div className="mt-4 space-y-3" data-id="element-882" data-mp="uty0pm2398on">
              <TextField label={t('payerName')} value={data.payerName} onChange={v => update({
            payerName: v
          })} data-id="element-883" data-mp="usmt1b5ivgs9" />
              <TextField label={t('payerEmail')} type="email" value={data.payerEmail} onChange={v => update({
            payerEmail: v
          })} data-id="element-884" data-mp="3uaw7ieu62rxn" />
              <TextField label={t('payerPhone')} type="tel" value={data.payerPhone} onChange={v => update({
            payerPhone: v
          })} data-id="element-885" data-mp="73amkj1kxmtn" />
              {errors.payer && <p className="text-sm font-medium text-magenta-dark" data-id="element-886" data-mp="c483ytermcu3">{t(errors.payer)}</p>}
            </div>}
        </fieldset>
        <fieldset data-id="element-887" data-mp="1ni3lu9x20zp0">
          <legend className="mb-2 font-display text-lg font-extrabold text-ink" data-id="element-888" data-mp="1nhakxpks14es">{t('methodTitle')}</legend>
          <div role="radiogroup" aria-label={t('methodTitle')} className="grid gap-2" data-id="element-889" data-mp="380e4m1en0nq6">
            <ChoiceCard size="sm" selected={data.paymentMethod === 'card'} onSelect={() => update({
            paymentMethod: 'card'
          })} title={t('payCard')} icon={<CreditCardIcon className="h-5 w-5" data-id="element-891" data-mp="2y8qd60i2z6op" />} data-id="element-890" data-mp="330hzfyu7eq91" />
            <ChoiceCard size="sm" selected={data.paymentMethod === 'zelle'} onSelect={() => update({
            paymentMethod: 'zelle'
          })} title={t('payZelle')} icon={<LandmarkIcon className="h-5 w-5" data-id="element-893" data-mp="2pb7q6qkvmia6" />} data-id="element-892" data-mp="2tbrnbu5g9lwb" />
          </div>
          {data.paymentMethod === 'zelle' && <p className="mt-3 text-sm leading-relaxed text-ink-600" data-id="element-894" data-mp="2k98mffl85jcp">{t('zelleNote')}</p>}
        </fieldset>
      </section>

      <section className="mt-10" data-id="element-895" data-mp="2fda3w8mkuvk7">
        <h2 className="font-display text-lg font-extrabold text-ink" data-id="element-896" data-mp="2acvxsw4jdvot">{t('docsTitle')}</h2>
        <ul className="mt-3 divide-y divide-ink-200 rounded-2xl bg-white ring-1 ring-ink-200" data-id="element-897" data-mp="dklxjqemzs07">
          {docs.map(d => <li key={d.key} className="flex items-start gap-3 p-4" data-id="element-898" data-mp="8m26xcp3ls1i">
              <FileTextIcon className="mt-0.5 h-5 w-5 shrink-0 text-ink-400" aria-hidden="true" data-id="element-899" data-mp="231he7ao01nmn" />
              <div className="min-w-0 flex-1" data-id="element-900" data-mp="27ro5ji9wbm8l">
                <p className="font-semibold text-ink" data-id="element-901" data-mp="27tioir1lx4a1">{t(d.key)}</p>
                <p className="text-sm text-ink-500" data-id="element-902" data-mp="1t3tahv8f15sc">
                  {d.who} · {t(d.when)}
                </p>
              </div>
              <span className="text-right text-xs font-medium text-ink-500" data-id="element-903" data-mp="1y2o5dvjnqkst">{t('docStatusNot')}</span>
            </li>)}
        </ul>
      </section>

      <section className="mt-8 rounded-xl bg-ink-100 p-5" data-id="element-904" data-mp="2qrbrtug57g7y">
        <h2 className="font-semibold text-ink" data-id="element-905" data-mp="2qopj3rhimuo5">{t('cancelTitle')}</h2>
        <p className="mt-1 text-[15px] leading-relaxed text-ink-700" data-id="element-906" data-mp="2cq96xl0vwbym">{t('cancelBody')}</p>
        <p className="mt-3 text-[15px] font-semibold text-ink" data-id="element-907" data-mp="2grb5s0qw2m02">
          {t('lineDeposit')}: {usd(quote.deposit, lang)}. <span className="font-normal text-ink-600" data-id="element-908" data-mp="11aaqux7agpnw">{t('depositCredit')}</span>
        </p>
      </section>

      <section className="mt-8 rounded-2xl border-2 border-ink bg-white p-5 sm:p-6" data-id="element-909" data-mp="15es7inicqmug">
        <div className="grid gap-3 sm:grid-cols-2" data-id="element-910" data-mp="xsn3pfx7ihlp">
          {[t('reserve99'), t('prepay')].map(label => <button key={label} type="button" disabled={!checkoutReady} className="flex min-h-[56px] flex-col items-center justify-center rounded-xl border-2 border-ink-200 px-4 text-ink disabled:cursor-not-allowed disabled:bg-ink-100" data-id="element-911" data-mp="suliaocb6672">
              <span className="flex items-center gap-2 font-bold" data-id="element-912" data-mp="f36xqd2r5wxw">
                <LockIcon className="h-4 w-4 text-ink-400" aria-hidden="true" data-id="element-913" data-mp="f1cbmvfqvys8" />
                {label}
              </span>
              {!checkoutReady && <span className="text-xs font-medium text-ink-500" data-id="element-914" data-mp="otrv5x6owvf8">{t('notAvailable')}</span>}
            </button>)}
        </div>
        {siteConfig.shopifyCheckoutUrl && <a href={siteConfig.shopifyCheckoutUrl} onClick={goShopify} className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-ink-700 active:scale-[0.98]" data-id="element-915" data-mp="3sicxl2oqxxcs">
            <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-916" data-mp="36ztbgcc1d5b4" />
            {lang === 'es' ? `Pagar depósito de ${usd(quote.deposit, lang)} en Shopify` : `Pay ${usd(quote.deposit, lang)} deposit on Shopify`}
          </a>}
        {failed && <p role="alert" className="mt-4 rounded-lg bg-magenta-soft p-3 text-sm font-medium text-ink" data-id="element-917" data-mp="3kyszpivmzi6r">
            {t('submitError')}
          </p>}
        <button type="button" onClick={send} disabled={sending} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-magenta text-[19px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98] disabled:opacity-70" data-id="element-918" data-mp="3pwbe3osfapm5">
          {sending && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-919" data-mp="ck16q7ziclob" />}
          {sending ? t('submitting') : failed ? t('retry') : t('requestEstimate')}
        </button>
        <p className="mt-3 text-sm text-ink-600" data-id="element-920" data-mp="1s1fopus1d3oa">{t('requestNote')}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm" data-id="element-921" data-mp="1s4a6tfnkucej">
          <a href={phones.ai.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-ink underline underline-offset-4" data-id="element-922" data-mp="1o0vp8kjzpnyz">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-923" data-mp="2fr9y22d5ca3s" />
            {t('callKyle')}: {phones.ai.display}
          </a>
          <a href={phones.human.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-ink underline underline-offset-4" data-id="element-924" data-mp="2bqzipz3gs7c9">
            {t('humanHelp')}: {phones.human.display}
          </a>
        </div>
      </section>

      <HelpAnswers screen="stepReserve" className="mt-10" data-id="element-925" data-mp="21y0tcf058crl" />
    </div>;
}